const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
const SKIP_HEADER = "x-skip-action-lock";
const INSTALL_FLAG = "__LH_MUTATION_FETCH_GUARD_INSTALLED__";

const pendingMutationRequests = new Map();
const formDataIds = new WeakMap();
let formDataIdSeq = 0;

function getFormDataIdentity(formData) {
    if (!formDataIds.has(formData)) {
        formDataIdSeq += 1;
        formDataIds.set(formData, formDataIdSeq);
    }
    return formDataIds.get(formData);
}

function normalizeUrl(input) {
    if (typeof input === "string") return input;
    if (input instanceof URL) return input.toString();
    if (input instanceof Request) return input.url;
    return String(input || "");
}

function isSkippable(headers) {
    const skipValue = headers.get(SKIP_HEADER);
    if (!skipValue) return false;
    const normalized = String(skipValue).trim().toLowerCase();
    return normalized === "1" || normalized === "true" || normalized === "yes";
}

function normalizeBody(input, init) {
    const initBody = init?.body;
    if (typeof initBody === "string") return initBody;
    if (initBody instanceof URLSearchParams) return initBody.toString();
    if (initBody instanceof FormData) {
        return `__FORMDATA__${getFormDataIdentity(initBody)}`;
    }
    if (initBody instanceof Blob) return `__BLOB__${initBody.size}`;
    if (initBody instanceof ArrayBuffer || ArrayBuffer.isView(initBody)) return "__BINARY__";
    if (initBody !== undefined && initBody !== null) return String(initBody);

    if (input instanceof Request) {
        return "__REQUEST_BODY__";
    }
    return "";
}

export function installMutationRequestGuard() {
    if (typeof window === "undefined" || typeof window.fetch !== "function") return;
    if (window[INSTALL_FLAG]) return;
    window[INSTALL_FLAG] = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = async (input, init = {}) => {
        const method = String(
            init?.method || (input instanceof Request ? input.method : "GET")
        ).toUpperCase();

        if (!MUTATING_METHODS.has(method)) {
            return originalFetch(input, init);
        }

        const mergedHeaders = new Headers(input instanceof Request ? input.headers : undefined);
        const requestHeaders = new Headers(init?.headers || {});
        requestHeaders.forEach((value, key) => mergedHeaders.set(key, value));

        if (isSkippable(mergedHeaders)) {
            return originalFetch(input, init);
        }

        const key = [
            method,
            normalizeUrl(input),
            normalizeBody(input, init),
        ].join("::");

        const existing = pendingMutationRequests.get(key);
        if (existing) {
            const replayResponse = await existing;
            return replayResponse.clone();
        }

        const trackedPromise = originalFetch(input, init)
            .then((response) => {
                const replay = response.clone();
                return { primary: response, replay };
            })
            .finally(() => {
                pendingMutationRequests.delete(key);
            });

        const replayPromise = trackedPromise.then((pair) => pair.replay);
        // Mark the internal replay promise as handled to avoid unhandled
        // rejection noise when the original fetch fails and no duplicate call
        // awaits this entry.
        replayPromise.catch(() => {});
        pendingMutationRequests.set(key, replayPromise);

        const result = await trackedPromise;
        return result.primary;
    };
}
